'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams } from 'next/navigation';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { Dialog, Button, Input, Search } from 'src/components';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faXmark, faUserGroup } from '@fortawesome/pro-regular-svg-icons';
import useGetAllPermissions from 'src/hooks/roles/useGetAvailablePermissions';
import useRoles from 'src/hooks/roles/useRoles';
import { useGetCompanyRolesQuery } from 'src/api/redux';
import { createRole, updateRole } from 'src/api';
import PermissionTable from './components/PermissionTable';

type Permission = {
  code: string;
  description: string;
};

type PermissionResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Permission[];
};

type AddEditRolesModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const AddEditRolesModal = ({ isOpen, onClose }: AddEditRolesModalProps) => {
  const { companyId } = useParams<{ companyId: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();
  // We'll manage the dialog state in the parent component
  const { editOrCopyRole, setEditOrCopyRole } = useRoles();

  const [label, setLabel] = useState(editOrCopyRole?.label || '');
  const [description, setDescription] = useState(
    editOrCopyRole?.description || ''
  );
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    editOrCopyRole?.permissions || []
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { refetch } = useGetCompanyRolesQuery(
    { companyId },
    { skip: !companyId }
  );
  const { data: allPermissions } = useGetAllPermissions();
  const typedPermissions = allPermissions as PermissionResponse | undefined;

  useEffect(() => {
    if (!editOrCopyRole) return;
    setLabel(editOrCopyRole.label || '');
    setDescription(editOrCopyRole.description || '');
    setSelectedPermissions(editOrCopyRole.permissions || []);
  }, [editOrCopyRole]);

  const permissionGroups = useMemo(() => {
    if (!typedPermissions?.results) return [];

    // Group permissions by their resource (second segment)
    const groupedPermissions = typedPermissions.results.reduce(
      (acc: { [key: string]: Permission[] }, permission) => {
        const segments = permission.code.split('.');
        const resourceKey = segments[1] || 'other'; // Use 'other' if no resource segment
        if (!acc[resourceKey]) {
          acc[resourceKey] = [];
        }
        acc[resourceKey].push(permission);
        return acc;
      },
      {}
    );

    // Convert grouped permissions into array and sort permissions within each group
    return Object.entries(groupedPermissions)
      .map(([key, permissions]) => {
        // Convert resource_name to Resource Name
        const groupName = key
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        // Sort permissions by action priority (read, write, delete) and scope
        const sortedPermissions = permissions.sort((a, b) => {
          const [aAction] = a.code.split('.');
          const [bAction] = b.code.split('.');

          // Define action priority
          const actionPriority: { [key: string]: number } = {
            read: 1,
            write: 2,
            delete: 3,
            export: 4,
            show: 5,
          };

          return (
            (actionPriority[aAction] || 99) - (actionPriority[bAction] || 99)
          );
        });

        return {
          group: groupName,
          description: `Manage ${groupName.toLowerCase()} permissions`,
          permissions: sortedPermissions,
        };
      })
      .sort((a, b) => a.group.localeCompare(b.group)); // Sort groups alphabetically
  }, [typedPermissions]);

  // Helper function to create a human-readable label from permission code
  const createPermissionLabel = useCallback((code: string): string => {
    const parts = code.split('.');
    if (parts.length < 2) return code;

    const action = parts[0];
    const scope = parts[2];

    const actionMap: { [key: string]: string } = {
      read: 'View',
      write: 'Edit',
      delete: 'Delete',
      export: 'Export',
      show: 'Show',
    };

    const scopeMap: { [key: string]: string } = {
      own: 'own',
      personal: 'personal',
      company: 'all',
      bounded: 'bounded',
      unbounded: 'unbounded',
      direct_report: "direct reports'",
      all: 'all',
    };

    const actionLabel = actionMap[action] || action;
    const scopeLabel = scopeMap[scope] || scope;

    return `${actionLabel} ${scopeLabel}`;
  }, []);

  const filteredPermissionGroups = useMemo(() => {
    if (!searchTerm.trim()) return permissionGroups;

    const normalizedSearch = searchTerm.toLowerCase().trim();

    return permissionGroups
      .map((group) => {
        // Filter permissions within each group
        const filteredPermissions = group.permissions.filter(
          (permission) =>
            permission.description.toLowerCase().includes(normalizedSearch) ||
            permission.code.toLowerCase().includes(normalizedSearch) ||
            createPermissionLabel(permission.code)
              .toLowerCase()
              .includes(normalizedSearch)
        );

        // If any permissions match or group name matches, return the group with filtered permissions
        if (
          filteredPermissions.length > 0 ||
          group.group.toLowerCase().includes(normalizedSearch) ||
          group.description.toLowerCase().includes(normalizedSearch)
        ) {
          return {
            ...group,
            permissions: filteredPermissions,
          };
        }

        // If no match, return null
        return null;
      })
      .filter((group): group is NonNullable<typeof group> => group !== null);
  }, [permissionGroups, searchTerm, createPermissionLabel]);

  const isDirty = useMemo(() => {
    return label !== '' && description !== '' && selectedPermissions.length > 0;
  }, [label, description, selectedPermissions]);

  const handleCancel = useCallback(() => {
    // The parent component will handle closing the dialog
    setLabel('');
    setDescription('');
    setSelectedPermissions([]);
    setEditOrCopyRole(null);
    onClose();
  }, [onClose, setEditOrCopyRole]);

  const handleLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLabel(e.target.value);
    },
    []
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDescription(e.target.value);
    },
    []
  );

  const handleSelectPermission = useCallback((permissionKey: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionKey)
        ? prev.filter((p) => p !== permissionKey)
        : [...prev, permissionKey]
    );
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleReset = useCallback(() => {
    setLabel('');
    setDescription('');
    setSelectedPermissions([]);
  }, []);

  const onSubmit = useCallback(async () => {
    setIsLoading(true);
    try {
      // If the role has an id it is an edit, else it's a copied or newly created role
      if (editOrCopyRole?.id) {
        await updateRole({
          companyId,
          roleId: editOrCopyRole?.id,
          data: {
            label,
            description,
            permissions: selectedPermissions,
          },
        });
        setNotification({
          title: 'Success',
          message: 'Role updated successfully',
          type: 'success',
        });
      } else {
        await createRole({
          companyId,
          data: {
            label,
            description,
            permissions: selectedPermissions,
          },
        });
        setNotification({
          title: 'Success',
          message: 'Role created successfully',
          type: 'success',
        });
      }
    } catch (err) {
      log.exception(
        `Failed to ${editOrCopyRole?.id ? 'update' : 'create'} role`,
        err.error_message
      );
      setNotification({
        title: 'Error',
        message: err.error_message || 'Failed to create role',
        type: 'error',
      });
    } finally {
      refetch();
      setIsLoading(false);
      setEditOrCopyRole(null);
      onClose();
      handleReset();
    }
  }, [
    editOrCopyRole?.id,
    companyId,
    label,
    description,
    selectedPermissions,
    setNotification,
    log,
    refetch,
    setEditOrCopyRole,
    onClose,
    handleReset,
  ]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      size="xl"
      className="h-auto max-w-6xl w-full"
    >
      <div className="flex flex-col h-[90vh] max-h-[900px]">
        <Dialog.Header className="bg-sky-600 text-white border-b-0 flex-none">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Icon icon={faUserGroup} className="text-white text-sm" />
              <div
                className="text-lg font-semibold"
                id="role-modal-title"
                data-testid="role-modal-title"
              >
                {editOrCopyRole?.id ? 'Edit' : 'Add'} Role
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IconButton
                variant="plain"
                color="neutral"
                size="sm"
                onClick={handleCancel}
                icon={faXmark}
                className="text-white hover:bg-sky-800"
                aria-label="Close"
              />
            </div>
          </div>
        </Dialog.Header>
        <Dialog.Content className="flex-1 overflow-auto">
          <div className="flex flex-col h-full">
            <div className="flex flex-col gap-4 p-4 flex-none">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <div className="text-sm font-medium text-neutral-700">
                    Name
                  </div>
                  <Input
                    autoFocus
                    placeholder="Enter role name (e.g. 'my role')"
                    className="rounded-full"
                    value={label}
                    onChange={handleLabelChange}
                    data-testid="role-name-input"
                    size="md"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="text-sm font-medium text-neutral-700">
                    Description
                  </div>
                  <Input
                    placeholder="Enter role description (e.g. 'what my role can do')"
                    className="rounded-full"
                    value={description}
                    onChange={handleDescriptionChange}
                    data-testid="role-description-input"
                    size="md"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Search
                  placeholder="Search permissions..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onSearch={handleSearch}
                  className="rounded-full w-80"
                  data-testid="permissions-search"
                  size="sm"
                />
              </div>
            </div>

            <div className="flex-1 min-h-0 px-4 pb-4">
              <div className="flex flex-col border border-neutral-200 rounded-xl h-full">
                <PermissionTable
                  permissionGroups={filteredPermissionGroups}
                  selectedPermissions={selectedPermissions}
                  onSelectPermission={handleSelectPermission}
                />
              </div>
            </div>
          </div>
        </Dialog.Content>
        <Dialog.Actions className="p-4 flex-none">
          <div className="flex items-center justify-between w-1/3 gap-2">
            <Button
              variant="outlined"
              color="neutral"
              size="lg"
              className="rounded-full w-full"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              size="lg"
              className="rounded-full w-full"
              onClick={onSubmit}
              disabled={!isDirty}
              loading={isLoading}
              data-testid="save-role-button"
            >
              Save
            </Button>
          </div>
        </Dialog.Actions>
      </div>
    </Dialog>
  );
};

export default AddEditRolesModal;
